import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { LOAN_PROGRAMS } from "../lib/constants";
import { ShieldCheck, Award, Briefcase } from "lucide-react";
import LanguageToggle from "../components/LanguageToggle";
import Logo from "../components/Logo";
import { useT } from "../lib/i18n";

export default function BrokerLanding() {
  const { slug } = useParams();
  const { t } = useT();
  const [broker, setBroker] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/broker/${slug}`);
        setBroker(data);
      } catch (e) {
        console.error("Failed to load broker landing:", e);
        setErr("Broker not found");
      }
      try {
        await api.post(`/public/track-referral/${slug}`);
      } catch (e) {
        console.error("Referral tracking failed (non-critical):", e);
      }
    })();
  }, [slug]);

  if (err) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-300">
        <div>
          <div className="font-display text-4xl">Broker Not Found</div>
          <Link to="/"><Button className="mt-6 bg-emerald-600 text-white hover:bg-emerald-500">Back home</Button></Link>
        </div>
      </div>
    );
  }
  if (!broker) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-400 text-sm">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-slate-950/70 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="pub-home-link">
            <Logo height={88} />
          </Link>
          <div className="flex items-center gap-3">
          <Link to={`/apply?ref=${slug}`}>
            <Button className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="pub-start-application-btn">
              {t("pub.startApplication")}
            </Button>
          </Link>
          <LanguageToggle />
          </div>
        </div>
      </header>

      <section className="relative">
        <div className="absolute inset-0 -z-10 opacity-30"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1646228148984-6d9de4b6edb2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwYW5kJTIwZ29sZCUyMGJhY2tncm91bmQlMjB0ZXh0dXJlfGVufDB8fHx8MTc3NzA1MjAyOXww&ixlib=rb-4.1.0&q=85")',
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-slate-950/80 via-slate-950/90 to-slate-950" />
        <div className="mx-auto grid max-w-6xl gap-10 px-6 py-24 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[11px] uppercase tracking-widest text-emerald-300">
              <ShieldCheck className="h-3 w-3" /> Certified Marketing Partner
            </div>
            <h1 className="font-display text-5xl font-medium leading-tight md:text-6xl" data-testid="broker-name-hero">
              {broker.name}
            </h1>
            {broker.company && (
              <div className="mt-2 text-lg text-slate-300">{broker.company}</div>
            )}
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">{broker.bio}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={`/apply?ref=${slug}`}>
                <Button size="lg" className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid="cta-primary-btn">
                  Start an Application
                </Button>
              </Link>
              <Link to="#programs">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-slate-700 bg-slate-900/60 text-slate-100 hover:bg-slate-800"
                  data-testid="cta-programs-btn"
                >
                  See Programs
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-lg border border-slate-800 bg-slate-900 p-6">
              <div className="mb-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">At a glance</div>
              <div className="grid grid-cols-2 gap-4">
                <Stat label="Years in RE" value={broker.years_experience || 0} Icon={Briefcase} />
                <Stat label="Deals closed" value={broker.total_deals || 0} Icon={Award} />
              </div>
              {Array.isArray(broker.specialties) && broker.specialties.length > 0 && (
                <div className="mt-6">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Specialties</div>
                  <div className="flex flex-wrap gap-2">
                    {broker.specialties.map((s) => (
                      <span key={s} className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-0.5 text-xs text-emerald-300" data-testid={`specialty-${s}`}>
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section id="programs" className="mx-auto max-w-6xl px-6 py-16">
        <div className="mb-10">
          <div className="mb-2 text-[11px] uppercase tracking-widest text-slate-500">Eight Programs</div>
          <h2 className="font-display text-3xl font-medium md:text-4xl">Funding built for operators</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {LOAN_PROGRAMS.map((p) => (
            <div key={p.key} className="rounded-lg border border-slate-800 bg-slate-900 p-5" data-testid={`pub-program-${p.key}`}>
              <div className="mb-3 flex items-baseline justify-between">
                <div className="text-xs font-semibold uppercase tracking-widest text-emerald-300">{p.rate}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Starting</div>
              </div>
              <div className="font-display text-lg">{p.name}</div>
              <div className="mt-2 text-sm text-slate-400">{p.tagline}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-6xl px-6 py-8 text-center text-xs text-slate-500">
          {broker.name} is a marketing partner of PMB Loans and does not originate, negotiate, or advise on loans.
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value, Icon }) {
  return (
    <div className="rounded-md border border-slate-800 bg-slate-950 p-4">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500">
        <Icon className="h-3.5 w-3.5 text-emerald-400" /> {label}
      </div>
      <div className="mt-2 font-display text-3xl">{value}</div>
    </div>
  );
}
